import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { authApi } from "../../api/auth";
import { formatApiError } from "../../lib/errors";
import { PASSWORD_HINT_KEY, PASSWORD_LIMITS, validatePassword, confirmError } from "../../lib/password";
import BrandHeader from "../BrandHeader";
import PasswordField from "./PasswordField";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("auth");

  const mismatch = confirmError(password, confirm) !== null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    // No top-level banner for this one: the inline message under the second
    // field and its red border already say it, and a banner survives the fix —
    // it would sit there claiming a mismatch above two fields that now agree.
    if (confirmError(password, confirm) || password !== confirm) {
      return;
    }
    const pwdError = validatePassword(password);
    if (pwdError) {
      setError(t(pwdError, PASSWORD_LIMITS));
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword({ token, password });
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err, t("resetPassword.errorFallback")));
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <BrandHeader />
        <div className="flex items-center justify-center min-h-[calc(100vh-6rem)] px-6">
          <div className="w-full max-w-md feature-card p-8 rounded-lg text-center">
            <h1 className="font-clash text-2xl font-bold text-white mb-4">{t("resetPassword.successTitle")}</h1>
            <p className="text-[#A1A1AA] mb-6">{t("resetPassword.successBody")}</p>
            <Link to="/login" className="text-[#FF5500] hover:text-[#FF7733] text-sm">{t("resetPassword.logIn")}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <BrandHeader />
      <div className="flex items-center justify-center min-h-[calc(100vh-6rem)] px-6">
        <div className="w-full max-w-md feature-card p-8 rounded-lg">
          <h1 className="font-clash text-2xl font-bold text-white mb-6 text-center">{t("resetPassword.title")}</h1>
          {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded mb-4 text-sm">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">{t("resetPassword.passwordLabel")}</label>
              <PasswordField value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password" required minLength={PASSWORD_LIMITS.min} />
              <p className="text-xs text-[#A1A1AA] mt-1">{t(PASSWORD_HINT_KEY, PASSWORD_LIMITS)}</p>
            </div>
            {/* Same reasoning as signup: this is a password being created, and a
                typo here sends them straight back to /forgot-password. */}
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">{t("password.confirmLabel")}</label>
              <PasswordField value={confirm} onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password" invalid={!!mismatch} required minLength={PASSWORD_LIMITS.min} />
              {mismatch && <p className="text-xs text-red-400 mt-1">{t("password.mismatch")}</p>}
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full py-3 rounded text-sm font-semibold disabled:opacity-50">
              {loading ? t("resetPassword.submitting") : t("resetPassword.submit")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
